import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { AxiosError } from 'axios'
import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { IReportReviewMacro, IReportReviewsResponse, IReview } from '~/types/review'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import CustomLongTextOption from '~/components/SelectOptions/CustomLongTextOption'
import { FORM_ERRORS } from '~/const'
import { keys as reviewsKeys, useReportReviewsMutation } from '~/queries/reviews'
import { useReviewsReportMacrosQuery } from '~/queries/reviews-report-macros'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { displayToast } from '~/utils/toast'
import { RequiredYupOption } from '~/utils/yup'

import './../styles.scss'

const MODAL_ID = 'report-review-modal'

interface FormValues {
  macro?: IOption | null
  reason: IOption | null
  subject: IOption | null
  text: string
}

const filterMacros = ({
  filters,
  macros,
}: {
  filters: { reason?: string; subject?: string }
  macros: IReportReviewMacro[]
}) => {
  return !filters.reason && !filters.subject
    ? macros
    : macros.filter(({ reason, subject }) =>
        filters.reason && filters.subject
          ? reason === filters.reason && subject === filters.subject
          : filters.reason
            ? reason === filters.reason
            : subject === filters.subject
      )
}

const defaultValues: FormValues = {
  macro: null,
  reason: null,
  subject: null,
  text: '',
}

const validationSchema = Yup.object().shape({
  macro: RequiredYupOption.nullable().notRequired(),
  reason: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
  subject: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
  text: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface ModalFormProps {
  onClose: VoidFunction
  onFailure: (errorMessage: string) => void
  onSuccess: (response: IReportReviewsResponse) => void
  reviews: IReview[]
}

export const getReportReviewErrorMessage = (error: AxiosError<IApiError>): string => {
  const apiError = error as AxiosError<IApiError>

  switch (apiError.response?.status) {
    case 409:
      return 'Review was already reported!'
    default:
      return 'Report cannot be created!'
  }
}

const ReportModalForm = ({ onClose, onFailure, onSuccess, reviews }: ModalFormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    resetField,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: report } = useReportReviewsMutation()

  const { data: macros = [] } = useReviewsReportMacrosQuery()

  const applicationsList = Array.from(new Set(reviews.map(({ app_key, device_type }) => ({ app_key, device_type }))))

  const macrosFilteredByAppKey = macros.filter(
    ({ applications, is_all_apps }) =>
      is_all_apps ||
      applications?.some((app) =>
        applicationsList.some(({ app_key, device_type }) => app.app_key === app_key && app.device_type === device_type)
      )
  )

  const reasonOptions = [...new Set(macrosFilteredByAppKey.map(({ reason }) => reason))].map((value) => ({
    label: value,
    value: value,
  }))

  const reasonWatch = watch('reason')

  const subjectWatch = watch('subject')

  const subjectOptions = [
    ...new Set(
      macrosFilteredByAppKey.filter(({ reason }) => reason === reasonWatch?.value).map(({ subject }) => subject)
    ),
  ].map((value) => ({
    label: value,
    value: value,
  }))

  const macroOptions = [
    ...new Set(
      macrosFilteredByAppKey.filter(
        ({ reason, subject }) => reason === reasonWatch?.value && subject === subjectWatch?.value
      )
    ),
  ].map(({ content }) => ({
    label: content,
    value: content,
  }))

  /** Handlers */

  const handleFormSubmit = async ({ reason, subject, text }: FormValues) => {
    if (text && reason && subject) {
      try {
        const response = await report({
          macro: text,
          reason: reason.value,
          review_ids: reviews.map(({ id }) => id),
          subject: subject.value,
        })
        await queryClient.invalidateQueries({ queryKey: reviewsKeys.all })
        onSuccess(response)
      } catch (e) {
        onFailure(getReportReviewErrorMessage(e as AxiosError<IApiError>))
      }
    } else if (reason && subject) {
      const filteredMacros = filterMacros({
        filters: { reason: reason.value, subject: subject.value },
        macros: macrosFilteredByAppKey,
      })

      if (filteredMacros.length) {
        const payload = {
          macro: filteredMacros[0].content,
          reason: reason.value,
          review_ids: reviews.map(({ id }) => id),
          subject: subject.value,
        }

        try {
          const response = await report(payload)
          await queryClient.invalidateQueries({ queryKey: reviewsKeys.all })
          onSuccess(response)
        } catch (e) {
          onFailure(getReportReviewErrorMessage(e as AxiosError<IApiError>))
        }
      } else {
        displayToast('No macro matches this concern and subject combination', 'error')
      }
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <div className="general-fieldset">
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.reason?.message}
                label="Concern"
                onChange={(option) => {
                  field.onChange(option)
                  resetField('subject')
                  resetField('macro')
                  resetField('text')
                }}
                options={reasonOptions}
                placeholder="Select a concern"
                required
              />
            )}
          />
          <Controller
            control={control}
            name="subject"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                disabled={!reasonWatch}
                error={errors.subject?.message}
                label="Subject"
                onChange={(option) => {
                  field.onChange(option)
                  resetField('macro')
                  resetField('text')
                }}
                options={subjectOptions}
                placeholder={!reasonWatch ? 'Select a concern first' : 'Select a subject'}
                required
              />
            )}
          />
          <Controller
            control={control}
            name="macro"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                components={{ Option: CustomLongTextOption }}
                disabled={!(reasonWatch && subjectWatch)}
                error={errors.macro?.message}
                label="Macro"
                onChange={(option) => {
                  field.onChange(option)
                  const textValue = (option as IOption | null)?.value
                  textValue ? setValue('text', textValue) : resetField('text')
                }}
                options={macroOptions}
                placeholder={!(reasonWatch && subjectWatch) ? 'Select a concern and a subject first' : 'Select a macro'}
              />
            )}
          />
          <TextArea {...register('text')} error={errors.text?.message} label="Text" placeholder="Enter text" rows={6} />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Report'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends Omit<ModalFormProps, 'reviews'> {
  isOpen: boolean
  reviews?: IReview[]
}

const ReportModal = ({ isOpen, onClose, onFailure, onSuccess, reviews }: ModalProps): ReactElement => {
  return (
    <Modal className="report-review-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Report Review</ModalHeader>
      {reviews ? (
        <ReportModalForm onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} reviews={reviews} />
      ) : null}
    </Modal>
  )
}

export default ReportModal
